import { cx } from "class-variance-authority";
import { useState } from "react";
import {
  getCorrectHtmlAttributesForInput,
  LabelProp,
  normalizeLabelProp,
} from "../input-general/label-prop";
import { useHtmlId } from "../utils/useHtmlId";
import InputLabel from "../input-general/input-label";
import ValidationMessage, {
  invalidInputOutlineClasses,
} from "../input-general/validation";

/**
 * @deprecated In FloKit
 */
export type TextViewHandler = (value: any, name?: string) => any;

/**
 * @deprecated In FloKit
 */
export interface TextViewProps {
  value: any;
  onChange: TextViewHandler;
  giveMeActualEventInOnChange?: boolean;
  alwaysShowValidation?: boolean;
  name?: string;
  label: LabelProp;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  required?: boolean;
  tooltip?: string;
  disabled?: boolean;
  inputRef?: any;
  maxLength?: number;
  validationMsg?: string;
  validation?: boolean;
  minValue?: number;
  maxValue?: number;
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  UNSAFE_inputClassName?: string;
  UNSAFEcontainerClassName?: string;
  UNSAFElabelClassName?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}
/**
 * @deprecated In FloKit
 */
export const TextView = ({
  validation,
  value,
  onChange,
  giveMeActualEventInOnChange,
  name,
  label,
  placeholder,
  type,
  autoFocus,
  required,
  tooltip,
  disabled,
  inputRef,
  maxLength,
  validationMsg,
  minValue,
  maxValue,
  onFocus,
  onBlur,
  // eslint-disable-next-line camelcase
  UNSAFE_inputClassName,

  UNSAFEcontainerClassName,

  UNSAFElabelClassName,
  ...props
}: TextViewProps) => {
  const generatedId = useHtmlId();

  const labelMethod = normalizeLabelProp(label);

  const [focused, setFocused] = useState(false);

  return (
    <div
      className={cx(
        "flex w-full flex-col",
        labelMethod.type !== "hidden" && "gap-y-2",
        UNSAFEcontainerClassName,
      )}
    >
      <InputLabel
        id={generatedId}
        labelMethod={labelMethod}
        name={name}
        required={required}
        tooltip={tooltip}
        isElementInteractive={true}
      />
      <input
        maxLength={maxLength}
        {...props}
        name={name}
        ref={inputRef}
        type={type}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        {...getCorrectHtmlAttributesForInput({
          labelProp: label,
          htmlId: generatedId,
          elementIsNonInteractive: false,
          fieldIsRequired: required,
        })}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={(e) => {
          setFocused(true);
          onFocus && onFocus(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur && onBlur(e);
        }}
        className={cx(
          "rounded border border-dublin-gray px-4 py-2 focus:border-flo-magenta focus:outline-none focus:ring-1 focus:ring-flo-magenta",
          validation && invalidInputOutlineClasses,
          // eslint-disable-next-line camelcase
          UNSAFE_inputClassName ? UNSAFE_inputClassName : "text-base",
        )}
        onChange={(e) => {
          // Because of 'value: any' in argument of onChange, we don't know whether its argument should be a string or a number
          try {
            if (type === "number")
              return onChange(
                parseInt(e.target.value, 10),
                e.target.name || "",
              );
          } catch (err) {
            // In case onChange already has parseInt already built in, then we just run onChange as normal
            if (err instanceof TypeError)
              return onChange(e.target.value, e.target.name || "");
          }

          return onChange(e.target.value, e.target.name || "");
        }}
        value={value}
        min={type === "number" && minValue !== undefined ? minValue : undefined}
        max={type === "number" && maxValue !== undefined ? maxValue : undefined}
      />
      <ValidationMessage
        shouldShowValidation={
          props.alwaysShowValidation ? validation : !focused && validation
        }
        validationMessage={validationMsg || "This field is required"}
      />
    </div>
  );
};
